import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  KnowledgeBase,
  KbListResponse,
  KbUpdateRequest,
  KbUploadRequest,
  KbConfig,
  KB_ACTIVE_STATUSES,
} from '@/types'
import { toast } from 'sonner'

const KB_QUERY_KEY = 'knowledge-bases'
const KB_CONFIG_QUERY_KEY = 'kb-config'

// The KB module lives at CORE-API's `/fp/kb` (change: excel-knowledge-bases,
// design D6) — NOT under the `/admin` prefix the shared `api` client defaults
// to (see src/services/api.ts). Passing an absolute URL makes axios ignore
// `api.defaults.baseURL` for just these calls while still reusing the same
// instance, so auth header injection, 401 refresh, and error-toast
// interceptors all keep working unchanged.
const KB_BASE_URL = `${import.meta.env.VITE_API_URL}/fp/kb`

/** System-owned KBs only — this admin screen manages `owner_type=system`
 * knowledge bases. `GET /fp/kb` also returns the caller's own KBs, filtered
 * out here since an admin's personal KBs (if any) belong on the user-facing
 * app, not this screen. */
export const useKbList = () => {
  return useQuery<KnowledgeBase[]>({
    queryKey: [KB_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get<KbListResponse>(KB_BASE_URL)
      return data.items.filter((kb) => kb.owner_type === 'system')
    },
    refetchInterval: (query) => {
      const items = query.state.data ?? []
      const hasActive = items.some((kb) => KB_ACTIVE_STATUSES.includes(kb.status))
      return hasActive ? 4000 : false
    },
  })
}

export const useKbConfig = () => {
  return useQuery<KbConfig>({
    queryKey: [KB_CONFIG_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get<KbConfig>(`${KB_BASE_URL}/config`)
      return data
    },
  })
}

export const useCreateKb = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: KbUploadRequest) => {
      const formData = new FormData()
      formData.append('file', request.file)
      formData.append('name', request.name)
      formData.append('owner_type', 'system')
      if (request.description) formData.append('description', request.description)
      if (request.keywords && request.keywords.length > 0) {
        formData.append('keywords', request.keywords.join(','))
      }
      const { data } = await api.post<KnowledgeBase>(KB_BASE_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KB_QUERY_KEY] })
      toast.success('Base de conocimiento creada')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail || error.message || 'Error al crear base de conocimiento'
      toast.error(msg)
    },
  })
}

export const useUpdateKb = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ kbId, updates }: { kbId: string; updates: KbUpdateRequest }) => {
      const { data } = await api.patch<KnowledgeBase>(`${KB_BASE_URL}/${kbId}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KB_QUERY_KEY] })
      toast.success('Base de conocimiento actualizada')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail ||
        error.message ||
        'Error al actualizar base de conocimiento'
      toast.error(msg)
    },
  })
}

export const useDeleteKb = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (kbId: string) => {
      await api.delete(`${KB_BASE_URL}/${kbId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KB_QUERY_KEY] })
      toast.success('Base de conocimiento eliminada')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail || error.message || 'Error al eliminar base de conocimiento'
      toast.error(msg)
    },
  })
}

/** Retry from `error` status — republishes indexing (spec: error-state
 * recovery). */
export const useReindexKb = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (kbId: string) => {
      const { data } = await api.post<KnowledgeBase>(`${KB_BASE_URL}/${kbId}/reindex`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KB_QUERY_KEY] })
      toast.success('Reintentando indexación')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail || error.message || 'Error al reintentar indexación'
      toast.error(msg)
    },
  })
}
